import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';
import { useCart } from '../context/CartContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [paymentDetails, setPaymentDetails] = useState(null);
  const { clearCart } = useCart();
  const navigate = useNavigate();

  const pollPaymentStatus = async (sessionId, attempts) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      const data = response.data;

      setPaymentDetails(data);

      if (data.paymentStatus === 'paid') {
        setStatus('success');
        clearCart();
        return;
      } else if (data.status === 'expired') {
        setStatus('expired');
        return;
      }

      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  };

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId, 0);
    } else {
      setStatus('error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[hsl(var(--cream))] grain-texture">
      <Navbar />

      <section className="pt-32 pb-16">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-2xl mx-auto bg-white rounded-3xl p-12 shadow-lg text-center">
            {status === 'checking' && (
              <div data-testid="payment-checking">
                <Loader2 className="w-16 h-16 text-[hsl(var(--earth-green))] animate-spin mx-auto mb-6" />
                <h1 className="font-heading text-3xl text-[hsl(var(--charcoal))] mb-4">Processing Payment</h1>
                <p className="font-body text-lg text-[hsl(var(--stone-gray))]">
                  Please wait while we confirm your payment...
                </p>
              </div>
            )}

            {status === 'success' && (
              <div data-testid="payment-success">
                <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-6" />
                <h1 className="font-heading text-3xl text-[hsl(var(--charcoal))] mb-4">Payment Successful!</h1>
                <p className="font-body text-lg text-[hsl(var(--stone-gray))] mb-8">
                  Thank you for your purchase. Your order has been confirmed!
                </p>
                {paymentDetails && (
                  <div className="bg-[hsl(var(--leaf-light))]/30 rounded-2xl p-6 mb-8">
                    <div className="flex justify-between font-body text-[hsl(var(--stone-gray))] mb-2">
                      <span>Amount Paid:</span>
                      <span className="font-semibold">${(paymentDetails.amount / 100).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between font-body text-[hsl(var(--stone-gray))]">
                      <span>Currency:</span>
                      <span className="font-semibold uppercase">{paymentDetails.currency}</span>
                    </div>
                  </div>
                )}
                <button
                  data-testid="continue-shopping-button"
                  onClick={() => navigate('/products')}
                  className="btn-primary bg-[hsl(var(--earth-green))] text-[hsl(var(--cream))] px-8 py-4 rounded-2xl font-accent font-semibold uppercase tracking-wider shadow-lg"
                >
                  Continue Shopping
                </button>
              </div>
            )}

            {(status === 'error' || status === 'expired' || status === 'timeout') && (
              <div data-testid="payment-error">
                <XCircle className="w-16 h-16 text-red-600 mx-auto mb-6" />
                <h1 className="font-heading text-3xl text-[hsl(var(--charcoal))] mb-4">Payment Failed</h1>
                <p className="font-body text-lg text-[hsl(var(--stone-gray))] mb-8">
                  {status === 'timeout'
                    ? 'Payment verification timed out. Please check your email for confirmation.'
                    : 'There was an issue processing your payment. Please try again.'}
                </p>
                <button
                  onClick={() => navigate('/checkout')}
                  className="btn-primary bg-[hsl(var(--earth-green))] text-[hsl(var(--cream))] px-8 py-4 rounded-2xl font-accent font-semibold uppercase tracking-wider shadow-lg"
                >
                  Back to Cart
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CheckoutSuccessPage;